import { Box, Grid, TextLink, Title } from '@factorialco/deprecated-design-system'
import { FinanceValueObjectsContactTypeEnum } from 'generated/resources/datatable.graphql'
import { ContactFragment } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { countryName } from 'modules/core/lib/countryList'
import i18n from 'modules/core/lib/i18n'

import { contactFullName } from 'modules/finance/lib/contactsUtils'
import InfoItem from 'modules/procurement/components/InfoItem'

interface Props {
  contact: ContactFragment | undefined
  editable?: boolean
  showTitle?: boolean
  showLinkToEdit?: boolean
  showBankAccountInfo?: boolean
  as: FinanceValueObjectsContactTypeEnum
}

const ContactInfo = ({
  contact,
  editable,
  showBankAccountInfo = false,
  showTitle = false,
  showLinkToEdit = false,
  as,
}: Props) => {
  const { navigation } = useNavigation()

  if (!contact) {
    return i18n.t('procurement.purchase_orders.detail.vendor.not_specified')
  }

  const formatAddress = ({ line1, line2, postalCode, city, state }: ContactFragment['address']) => {
    const attrs = line2 ? [line1, line2, postalCode, city, state] : [line1, postalCode, city, state]
    return attrs.map((atr) => atr || '-').join(', ')
  }

  const basicContactInfo = [
    {
      label: i18n.t('procurement.purchase_orders.detail.vendor.fiscal_name'),
      value: contactFullName(contact),
    },
    {
      label: i18n.t('procurement.purchase_orders.detail.vendor.tin'),
      value: contact?.taxId || '-',
    },
    {
      label: i18n.t('procurement.purchase_orders.detail.vendor.address'),
      value: formatAddress(contact?.address),
    },
    {
      label: i18n.t('procurement.purchase_orders.detail.vendor.country'),
      value: countryName(contact?.address?.countryCode) || '-',
    },
  ]

  const formatIban = (iban: string | null | undefined) => {
    if (!iban) return '-'

    const parts = iban.match(/.{1,4}/g)
    return parts ? parts.join(' ') : ''
  }

  const bankAccountInfo = [
    {
      label: i18n.t('procurement.purchase_orders.detail.vendor.iban'),
      value: formatIban(contact?.iban),
    },
    {
      label: i18n.t('procurement.purchase_orders.detail.vendor.bank_code'),
      value: contact?.bankCode || '-',
    },
  ]

  const contactInfo =
    showBankAccountInfo && contact.preferredPaymentMethod == 'banktransfer'
      ? basicContactInfo.concat(bankAccountInfo)
      : basicContactInfo

  const destinationUrl =
    as === 'client'
      ? navigation.finance.sales.clients.contactId.fullPath({ contactId: contact.id })
      : navigation.finance.spending.vendors.contactId.fullPath({ contactId: contact.id })

  return (
    <Box gap="s12">
      <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
        {showTitle && <Title variant="500" title={i18n.t('finance.vendors.vendor_detail.title')} />}
        {editable && showLinkToEdit && (
          <TextLink to={destinationUrl}>
            {i18n.t('procurement.purchase_orders.detail.vendor.edit')}
          </TextLink>
        )}
      </Box>
      <Grid variant="fixed">
        {contactInfo.map((item) => (
          <Box gridColSpan={{ xxs: 6 }} key={item.label}>
            <InfoItem label={item.label} textItem={item.value} />
          </Box>
        ))}
      </Grid>
    </Box>
  )
}

export default ContactInfo
